import AccessTimeIcon from '@mui/icons-material/AccessTime'
import LoginIcon from '@mui/icons-material/Login'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import SearchIcon from '@mui/icons-material/Search'
import ListIcon from '@mui/icons-material/List'
import RefreshIcon from '@mui/icons-material/Refresh'
import HeightIcon from '@mui/icons-material/Height'
import SyncIcon from '@mui/icons-material/Sync'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import SaveIcon from '@mui/icons-material/Save'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VideocamIcon from '@mui/icons-material/Videocam'
import LeaveButton from '../components/LeaveButton'
import { Box, Typography, Button, Chip, IconButton, Tooltip, CircularProgress, Popover, Slider, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import { styled } from '@mui/material/styles'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import CheckInDialogComponent from 'src/clinic/components/CheckIn'
import { CheckoutButtonComponent } from 'src/clinic/components/CheckIn/checkInComponents'
import CheckinRequestPromptDialog from 'src/mentor/components/CheckinRequestPromptDialog'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getMPDataWithParams } from 'src/mentor/services'
import { getTherapyReportLabelFromEntity } from 'src/mentor/components/TherapyReportMakingPage/therapyReportLabels'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  createEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'
import { MENTOR_CLINIC_BOOKING_TABS_LIST } from 'src/mentor/components/MentorClinicBookingsPage/util'
import MomentDatePicker from 'src/mentor/components/PrescriptionMakingPage/inputComponents/diagnosis/components/MomentDatePicker'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import ChamberRoomStatusIndicator from './ChamberRoomStatusIndicator'

const HeaderContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1200,
  background: 'transparent',
  backdropFilter: 'blur(4px) saturate(120%)',
  WebkitBackdropFilter: 'blur(4px) saturate(120%)',
  padding: '1px 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '6px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  minHeight: '32px',
  height: 'auto',
  [theme.breakpoints.down('md')]: {
    padding: '1px 8px',
    gap: '4px',
    minHeight: '30px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '1px 6px',
    gap: '3px',
    minHeight: '28px',
  },
}))

const GlassBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '2px 8px',
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderRadius: '6px',
  border: '1px solid rgba(103, 121, 227, 0.2)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.down('sm')]: {
    padding: '2px 6px',
    gap: '4px',
  },
}))

const DateNavButton = styled(IconButton)(({ theme }) => ({
  padding: '3px',
  borderRadius: '4px',
  color: '#6779e3',
  background: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(103, 121, 227, 0.2)',
  '&:hover': {
    background: 'rgba(103, 121, 227, 0.1)',
    borderColor: 'rgba(103, 121, 227, 0.4)',
    color: '#5567d8',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '2px',
    '& svg': {
      fontSize: '16px',
    },
  },
}))

const TodayButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: '2px 8px',
  borderRadius: '4px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '11px',
  color: '#6779e3',
  background: 'rgba(103, 121, 227, 0.1)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(103, 121, 227, 0.25)',
  '&:hover': {
    background: 'rgba(103, 121, 227, 0.2)',
    borderColor: 'rgba(103, 121, 227, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '2px 6px',
    fontSize: '10px',
  },
}))

const CheckoutButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  background: 'rgba(239, 68, 68, 0.15)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderRadius: '6px',
  border: '1.5px solid rgba(239, 68, 68, 0.3)',
  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
  [theme.breakpoints.down('sm')]: {
    padding: '2px 6px',
  },
}))

// Report Mode Styled Components
const PatientInfoChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 12px',
  background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.15) 0%, rgba(103, 121, 227, 0.08) 100%)',
  borderRadius: '8px',
  border: '1px solid rgba(103, 121, 227, 0.25)',
  [theme.breakpoints.down('sm')]: {
    padding: '3px 8px',
    gap: '5px',
  },
}))

const TimerChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 10px',
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.08) 100%)',
  borderRadius: '8px',
  border: '1px solid rgba(16, 185, 129, 0.25)',
  fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace",
  [theme.breakpoints.down('sm')]: {
    padding: '3px 6px',
  },
}))

const ReportActionButton = styled(Button)(({ theme, variant }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '12px',
  padding: '5px 12px',
  borderRadius: '8px',
  boxShadow: 'none',
  minWidth: 'auto',
  transition: 'all 0.2s ease',
  ...(variant === 'save' && {
    background: 'linear-gradient(135deg, #6779e3 0%, #5567d8 100%)',
    color: '#fff',
    border: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #5567d8 0%, #4456c7 100%)',
      boxShadow: '0 4px 12px rgba(103, 121, 227, 0.35)',
    },
    '&:disabled': {
      background: 'rgba(103, 121, 227, 0.4)',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  }),
  ...(variant === 'leave' && {
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#dc2626',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    '&:hover': {
      background: 'rgba(239, 68, 68, 0.2)',
      borderColor: 'rgba(239, 68, 68, 0.5)',
    },
  }),
  ...(variant === 'complete' && {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    border: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
    },
  }),
  [theme.breakpoints.down('sm')]: {
    padding: '4px 8px',
    fontSize: '11px',
  },
}))

// Session Timer Hook
const useSessionTimer = (startTime) => {
  const [duration, setDuration] = useState('00:00')
  
  useEffect(() => {
    if (!startTime) return
    
    const start = dayjs(startTime)
    
    const updateDuration = () => {
      const now = dayjs()
      const diff = now.diff(start, 'second')
      const hours = Math.floor(diff / 3600)
      const minutes = Math.floor((diff % 3600) / 60)
      const seconds = diff % 60
      
      if (hours > 0) {
        setDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
      } else {
        setDuration(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
      }
    }
    
    updateDuration()
    const interval = setInterval(updateDuration, 1000)
    
    return () => clearInterval(interval)
  }, [startTime])
  
  return duration
}

// Report type labels — used as the badge under the patient strip. The therapy_report entry is
// resolved dynamically (see resolveReportTypeLabel below) so nutrition events show as
// "Nutrition Consultation Report" while psychotherapy stays "Therapy Report".
const REPORT_TYPE_LABELS = {
  'screening_report': 'Screening Report',
  'prescription': 'Prescription',
  'therapy_report': 'Therapy Report',
  'assessment_report': 'Assessment Report',
  'past_prescriptions': 'Past Prescriptions',
  'patient_history': 'Patient History',
  'vitals': 'Vitals',
}

function resolveReportTypeLabel(menuId, bookedEvent) {
  if (menuId === 'therapy_report') {
    return getTherapyReportLabelFromEntity(bookedEvent)
  }
  return REPORT_TYPE_LABELS[menuId] || 'Report'
}

export default function SplitScreenHeader({
  chosenClinicData,
  chamberScreenStateNow,
  leaveThisSession,
  serverGivenNextAction,
  selectedTab,
  setSelectedTab,
  selectedDate,
  setSelectedDate,
  onPrevClick,
  onNextClick,
  handleGoToToday,
  isToday,
  clinicSchedules,
  // Action button callbacks
  onOpenPatientSearch,
  onOpenBookedSlotsDrawer,
  onRefreshBookings,
  isRefreshing = false,
  // Slot duration props
  eachSlotDurationInMins,
  setEachSlotDurationInMins,
  // Sync action
  onSyncNextAction,
  isSyncing = false,
  // Hamburger menu for main drawer
  onToggleMainDrawer,
  // Calendar drawer callback for mobile
  onOpenCalendarDrawer,
  // Report mode props
  selectedOptionPatientMenu,
  selectedPatientBookedEventData,
  auxiliaryData,
  onSaveReport,
  onLeaveSession,
  onCompleteSession,
  isSavingReport = false,
  isLeavingSession = false,
  voiceCallActionNode = null,
  // Chamber screen status props
  chamberScreenStatus = 'unknown',
  joinedChamberName = null,
}) {
  const navigate = useNavigate()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [checkedInTime, setCheckedInTime] = useState(null)
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [isPeekingIn, setIsPeekingIn] = useState(false)
  const [peekedRoomData, setPeekedRoomData] = useState(null)
  const [checkInModalOpen, setCheckInModalOpen] = useState(false)
  const [dialogRoomData, setDialogRoomData] = useState(null)
  const [requestCheckoutDialogOpen, setRequestCheckoutDialogOpen] = useState(false)
  const [requestCheckoutRoomData, setRequestCheckoutRoomData] = useState(null)
  const [requestCheckoutLoading, setRequestCheckoutLoading] = useState(false)
  const checkInModalOpenRef = useRef(false)

  // Slot duration slider state
  const [slotDurationAnchorEl, setSlotDurationAnchorEl] = useState(null)
  const slotDurationPopoverOpen = Boolean(slotDurationAnchorEl)
  const [localSlotDuration, setLocalSlotDuration] = useState(eachSlotDurationInMins || 30)
  const justAppliedRef = useRef(false)
  
  // Combined 3-dot menu state (merged tabs + actions)
  const [combinedMenuAnchorEl, setCombinedMenuAnchorEl] = useState(null)
  const combinedMenuOpen = Boolean(combinedMenuAnchorEl)

  // Calendar popover state (mobile)
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null)
  const calendarPopoverOpen = Boolean(calendarAnchorEl)

  const MIN_SLOT_DURATION = 5
  const MAX_SLOT_DURATION = 120
  
  // Report mode detection - check if we're viewing a report/form instead of calendar
  const isInReportMode = useMemo(() => {
    const reportModes = ['screening_report', 'prescription', 'therapy_report', 'assessment_report']
    return selectedOptionPatientMenu && reportModes.includes(selectedOptionPatientMenu)
  }, [selectedOptionPatientMenu])
  
  // Session timer for report mode
  const sessionStartTime = useMemo(() => {
    if (!selectedPatientBookedEventData) return null
    return selectedPatientBookedEventData?.startTime || 
           selectedPatientBookedEventData?.bookedEventStartTime || 
           selectedPatientBookedEventData?.createdTime ||
           new Date().toISOString()
  }, [selectedPatientBookedEventData])
  const sessionDuration = useSessionTimer(isInReportMode ? sessionStartTime : null)
  
  // Memoized patient info for report mode header
  const patientInfo = useMemo(() => {
    if (!selectedPatientBookedEventData) return null
    return {
      name: selectedPatientBookedEventData?.patientInfoData?.name || 
            selectedPatientBookedEventData?.patientName || 
            selectedPatientBookedEventData?.userName || 
            'Patient',
      age: getAgeFromDobWithTime(
        selectedPatientBookedEventData?.patientInfoData?.dateOfBirth ||
        selectedPatientBookedEventData?.patientDateOfBirth
      ),
      gender: GENDER_LIST_ONBOARDING?.find(
        (item) => item.value === (
          selectedPatientBookedEventData?.patientInfoData?.gender ||
          selectedPatientBookedEventData?.patientGender
        ),
      )?.label || '',
      patientNumber: selectedPatientBookedEventData?.patientInfoData?.patientNumber ||
                     selectedPatientBookedEventData?.patientNumber || '',
      visitNumber: auxiliaryData?.consultationDetails?.visitNumber || 
                   selectedPatientBookedEventData?.patientMeetingSeqNumber || '1',
    }
  }, [selectedPatientBookedEventData, auxiliaryData])
  
  // Meeting type for header indicator
  const meetingType = useMemo(() => {
    const src = selectedPatientBookedEventData?.meetingSrc
    if (src === 'offline') {
      return { label: 'In-Person', color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.12)', icon: LocationOnIcon }
    } else if (src === 'aumhum' || src === 'online') {
      return { label: 'Online', color: '#2563eb', bgColor: 'rgba(37, 99, 235, 0.12)', icon: VideocamIcon }
    }
    return { label: 'Session', color: '#6779e3', bgColor: 'rgba(103, 121, 227, 0.12)', icon: AccessTimeIcon }
  }, [selectedPatientBookedEventData?.meetingSrc])

  // Initialize local state when popover opens
  useEffect(() => {
    if (slotDurationPopoverOpen && eachSlotDurationInMins !== undefined) {
      if (!justAppliedRef.current) {
        setLocalSlotDuration(eachSlotDurationInMins)
      }
    }
  }, [slotDurationPopoverOpen, eachSlotDurationInMins])

  // Reset the flag when popover closes
  useEffect(() => {
    if (!slotDurationPopoverOpen) {
      justAppliedRef.current = false
    }
  }, [slotDurationPopoverOpen])

  const handleSlotDurationClick = (event) => {
    const target = event?.currentTarget || (typeof event === 'object' && event?.currentTarget ? event.currentTarget : event)
    setSlotDurationAnchorEl(target)
    if (eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }

  const handleSlotDurationClose = () => {
    setSlotDurationAnchorEl(null)
    if (!justAppliedRef.current && eachSlotDurationInMins !== undefined) {
      setLocalSlotDuration(eachSlotDurationInMins)
    }
  }

  const handleSlotDurationChange = (event, newValue) => {
    setLocalSlotDuration(newValue)
  }

  const handleSlotDurationApply = () => {
    if (setEachSlotDurationInMins) {
      setEachSlotDurationInMins(localSlotDuration)
      justAppliedRef.current = true
      handleSlotDurationClose()
    }
  }

  // Check check-in status
  useEffect(() => {
    const interval = setInterval(() => {
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      const peekedRoomDetails = localStorage.getItem('peekedRoomDetails')

      if (checkedInDetails) {
        try {
          const parsed = JSON.parse(checkedInDetails)
          // Check for createdTime (actual field from API) or checkedInAt
          const checkInTime = parsed?.createdTime || parsed?.checkedInAt
          if (checkInTime) {
            // Parse as UTC and convert to local time for display
            setCheckedInTime(dayjs.utc(checkInTime).local())
            setIsCheckedIn(true)
            setIsPeekingIn(false)
          } else {
            setIsCheckedIn(false)
          }
        } catch (e) {
          setIsCheckedIn(false)
        }
      } else {
        setIsCheckedIn(false)
      }

      if (peekedRoomDetails && !checkedInDetails) {
        try {
          const parsed = JSON.parse(peekedRoomDetails)
          setPeekedRoomData(parsed)
          setIsPeekingIn(true)
        } catch (e) {
          setIsPeekingIn(false)
        }
      } else {
        setIsPeekingIn(false)
        setPeekedRoomData(null)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const openRequestCheckoutDialog = useCallback((roomData) => {
    setRequestCheckoutRoomData(roomData)
    setRequestCheckoutDialogOpen(true)
  }, [])

  const handleCheckInPrompt = useCallback(async () => {
    if (!peekedRoomData) {
      toast.error('No room data available')
      return
    }

    if (!chosenClinicData?.id) {
      toast.error('No clinic information available.')
      return
    }

    try {
      // Fetch fresh room status from the API
      const response = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
        clinicLocationId: chosenClinicData.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        const rooms = response?.data?.data?.['defined-room-access'] || []
        const currentRoom = rooms.find((room) => room?.id === peekedRoomData?.id)

        if (!currentRoom) {
          toast.error('Room not found. Please try again.')
          return
        }

        // Check if room is occupied by someone else using fresh data
        const roomOccupied = currentRoom?.occupied
        const occupiedByThisMentor = 
          roomOccupied && currentRoom?.checkedInMentorId === mentorProfileData?.id
        const checkedInMentorName = currentRoom?.checkedInMentorName

        if (roomOccupied && !occupiedByThisMentor) {
          const updatedRoomData = {
            ...peekedRoomData,
            occupied: currentRoom.occupied,
            checkedInMentorId: currentRoom.checkedInMentorId,
            checkedInMentorName: currentRoom.checkedInMentorName,
          }
          localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
          setPeekedRoomData(updatedRoomData)
          openRequestCheckoutDialog(updatedRoomData)
          return
        }

        // Update localStorage with fresh room data before proceeding
        const updatedRoomData = {
          ...peekedRoomData,
          occupied: currentRoom.occupied,
          checkedInMentorId: currentRoom.checkedInMentorId,
          checkedInMentorName: currentRoom.checkedInMentorName,
        }
        localStorage.setItem('peekedInRoomDetails', JSON.stringify(updatedRoomData))
        setPeekedRoomData(updatedRoomData)

        setDialogRoomData({ ...updatedRoomData })
        setCheckInModalOpen(true)
      } else {
        // If API call fails, fall back to using cached data but show a warning
        toast.warning('Could not refresh room status. Using cached information.', { autoClose: 3000 })
        
        // Check if room is occupied by someone else using cached data
        if (peekedRoomData?.occupied && peekedRoomData?.checkedInMentorId !== mentorProfileData?.id) {
          openRequestCheckoutDialog(peekedRoomData)
          return
        }

        setDialogRoomData({ ...peekedRoomData })
        setCheckInModalOpen(true)
      }
    } catch (error) {
      console.error('Error fetching room status:', error)
      toast.error('Failed to refresh room status. Please try again.', { autoClose: 3000 })
      
      // Fall back to using cached data
      if (peekedRoomData?.occupied && peekedRoomData?.checkedInMentorId !== mentorProfileData?.id) {
        openRequestCheckoutDialog(peekedRoomData)
        return
      }

      setDialogRoomData({ ...peekedRoomData })
      setCheckInModalOpen(true)
    }
  }, [peekedRoomData, mentorProfileData, chosenClinicData, openRequestCheckoutDialog])

  const handleCloseCheckInDialog = useCallback(() => {
    setCheckInModalOpen(false)
    setTimeout(() => {
      setDialogRoomData(null)
    }, 300)
  }, [])

  useEffect(() => {
    checkInModalOpenRef.current = checkInModalOpen
  }, [checkInModalOpen])

  const checkInDialogProps = useMemo(() => {
    if (!dialogRoomData) return null

    return {
      closeFunction: handleCloseCheckInDialog,
      onChoosingInPerson: () => {
        handleCloseCheckInDialog()
      },
      onChoosingRemote: () => {
        handleCloseCheckInDialog()
      },
      roomData: dialogRoomData,
      clinicLocationData: chosenClinicData,
      showJoinHotline: false,
      hotlineOnClick: () => {},
    }
  }, [dialogRoomData, chosenClinicData, handleCloseCheckInDialog])

  const handleSubmitCheckoutRequest = async (reason) => {
    if (!requestCheckoutRoomData?.id) {
      toast.error('No occupied room found for this request.')
      return
    }

    setRequestCheckoutLoading(true)

    try {
      const { request, response } = await createEmrCheckInRequest({
        chamberRoomId: requestCheckoutRoomData.id,
        reason,
      })

      if (request) {
        setRequestCheckoutDialogOpen(false)
        setRequestCheckoutRoomData(null)
        toast.success(
          `Checkout request sent to ${
            requestCheckoutRoomData?.checkedInMentorName || 'the checked-in mentor'
          }.`,
        )
        return
      }

      toast.error(
        getCheckInRequestErrorMessage(response, 'Could not send the checkout request.'),
      )
    } catch (error) {
      console.error('Error sending checkout request:', error)
      toast.error('Could not send the checkout request.')
    } finally {
      setRequestCheckoutLoading(false)
    }
  }
  
  const handleOpenCombinedMenu = (event) => {
    setCombinedMenuAnchorEl(event.currentTarget)
  }
  
  const handleCloseCombinedMenu = () => {
    setCombinedMenuAnchorEl(null)
  }
  
  const handleTabSelect = (tabId) => {
    setSelectedTab(tabId)
    handleCloseCombinedMenu()
  }

  const handleOpenCalendarPopover = (event) => {
    setCalendarAnchorEl(event.currentTarget)
  }

  const handleCloseCalendarPopover = () => {
    setCalendarAnchorEl(null)
  }
  
  const availableTabs = MENTOR_CLINIC_BOOKING_TABS_LIST?.filter((tab) => {
    return tab.id === 'calendar' || tab.id === 'appointments'
  })

  // Render report mode header when viewing a report
  if (isInReportMode && patientInfo && selectedOptionPatientMenu) {
    const MeetingIcon = meetingType.icon
    return (
      <>
        <HeaderContainer>
          {/* Left: Patient Info and Session Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1.5 }, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
            {/* Patient Info */}
            <PatientInfoChip>
              <PersonIcon sx={{ color: '#6779e3', fontSize: { xs: 16, md: 18 } }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '12px', md: '14px' },
                    color: '#1e293b',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {patientInfo.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '10px', md: '11px' },
                    color: '#64748b',
                  }}
                >
                  {patientInfo.age && patientInfo.gender 
                    ? `${patientInfo.age} • ${patientInfo.gender}` 
                    : patientInfo.age || patientInfo.gender || ''}
                  {patientInfo.patientNumber && ` • #${patientInfo.patientNumber}`}
                </Typography>
              </Box>
            </PatientInfoChip>
            
            {/* Report Type Badge */}
            <Chip
              label={resolveReportTypeLabel(selectedOptionPatientMenu, selectedPatientBookedEventData)}
              size="small"
              sx={{
                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                color: '#6779e3',
                fontWeight: 600,
                fontSize: { xs: '10px', md: '11px' },
                height: { xs: '22px', md: '24px' },
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            
            {/* Visit Number */}
            <Chip
              label={`Visit #${patientInfo.visitNumber}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(103, 121, 227, 0.08)',
                color: '#6779e3',
                fontWeight: 500,
                fontSize: { xs: '10px', md: '11px' },
                height: { xs: '22px', md: '24px' },
                display: { xs: 'none', md: 'flex' },
              }}
            />
            
            {/* Meeting Type Indicator */}
            <Chip
              icon={<MeetingIcon sx={{ fontSize: 14, color: meetingType.color }} />}
              label={meetingType.label}
              size="small"
              sx={{
                backgroundColor: meetingType.bgColor,
                color: meetingType.color,
                fontWeight: 600,
                fontSize: { xs: '10px', md: '11px' },
                height: { xs: '22px', md: '24px' },
                border: `1px solid ${meetingType.color}25`,
                '& .MuiChip-icon': { marginLeft: '4px' },
              }}
            />
            
            {/* Session Timer */}
            <TimerChip>
              <AccessTimeIcon sx={{ color: '#10b981', fontSize: { xs: 14, md: 16 } }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '11px', md: '13px' },
                  color: '#10b981',
                }}
              >
                {sessionDuration}
              </Typography>
            </TimerChip>
          </Box>
          
          {/* Right: Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
            {voiceCallActionNode}
            {/* Save Button */}
            {onSaveReport && (
              <ReportActionButton
                variant="save"
                onClick={onSaveReport}
                disabled={isSavingReport}
                startIcon={isSavingReport ? (
                  <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                ) : (
                  <SaveIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                )}
                sx={{ display: { xs: 'none', sm: 'flex' } }}
              >
                {isSavingReport ? 'Saving...' : 'Save'}
              </ReportActionButton>
            )}
            
            {/* Mobile: Save icon only */}
            {onSaveReport && (
              <Tooltip title="Save Report">
                <IconButton
                  size="small"
                  onClick={onSaveReport}
                  disabled={isSavingReport}
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    color: '#6779e3',
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                    border: '1px solid rgba(103, 121, 227, 0.2)',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    },
                  }}
                >
                  {isSavingReport ? (
                    <CircularProgress size={14} sx={{ color: '#6779e3' }} />
                  ) : (
                    <SaveIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            
            {/* For Screening & Therapy: Use LeaveButton with popover (Save Draft & Save & Complete options) */}
            {(selectedOptionPatientMenu === 'screening_report' || selectedOptionPatientMenu === 'therapy_report') && onLeaveSession ? (
              <LeaveButton
                leaveSession={async (markAsCompleted = false) => {
                  await onLeaveSession(markAsCompleted)
                }}
                isAlreadyMarkedAsCompleted={selectedPatientBookedEventData?.markedCompleted === true}
                isLeavingSession={isLeavingSession}
              >
                Leave
              </LeaveButton>
            ) : (
              <>
                {/* Leave Button - For other report types */}
                {onLeaveSession && (
                  <ReportActionButton
                    variant="leave"
                    onClick={onLeaveSession}
                    disabled={isLeavingSession}
                    startIcon={isLeavingSession ? (
                      <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    ) : (
                      <ExitToAppIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                    )}
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    {isLeavingSession ? 'Leaving...' : 'Leave'}
                  </ReportActionButton>
                )}
                
                {/* Mobile: Leave icon only */}
                {onLeaveSession && (
                  <Tooltip title={isLeavingSession ? 'Leaving...' : 'Leave Session'}>
                    <IconButton
                      size="small"
                      onClick={onLeaveSession}
                      disabled={isLeavingSession}
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        color: '#dc2626',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        padding: '6px',
                        '&:hover': {
                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                        },
                      }}
                    >
                      {isLeavingSession ? (
                        <CircularProgress size={16} sx={{ color: '#dc2626' }} />
                      ) : (
                        <ExitToAppIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                
                {/* Complete Button - For other report types */}
                {onCompleteSession && (
                  <ReportActionButton
                    variant="complete"
                    onClick={onCompleteSession}
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: { xs: 14, md: 16 } }} />}
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    Complete
                  </ReportActionButton>
                )}
              </>
            )}
            
            {/* Mobile: Complete icon only */}
            {onCompleteSession && (
              <Tooltip title="Complete Session">
                <IconButton
                  size="small"
                  onClick={onCompleteSession}
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    color: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </HeaderContainer>

        {/* Spacer for fixed header */}
        <Box sx={{ height: { xs: '32px', sm: '34px', md: '36px' } }} />
      </>
    )
  }
  
  // Default header for calendar/non-report mode
  return (
    <>
      <HeaderContainer>
        {/* Left: Clinic & Mentor Info with Check-in Time (Merged) - Hidden on mobile */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
          <GlassBox sx={{ gap: '4px', flexWrap: 'wrap' }}>
            <LocationOnIcon sx={{ fontSize: { xs: 14, sm: 16, md: 18 }, color: '#6779e3', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#6779e3',
                  fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '-0.01em',
                }}
                title={chosenClinicData?.title || chosenClinicData?.name}
              >
                {chosenClinicData?.title || chosenClinicData?.name}
              </Typography>
              {mentorProfileData && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(103, 121, 227, 0.75)',
                    fontSize: { xs: '9px', sm: '10px' },
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={mentorProfileData?.name || mentorProfileData?.firstName}
                >
                  {mentorProfileData?.name || mentorProfileData?.firstName}
                  {mentorProfileData?.specialization && ` • ${mentorProfileData.specialization}`}
                </Typography>
              )}
            </Box>

            {/* Check-in Time (merged into same card) */}
          {checkedInTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 'auto' }}>
                <AccessTimeIcon sx={{ fontSize: { xs: 11, sm: 12 }, color: 'rgba(16, 185, 129, 0.8)' }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(16, 185, 129, 0.9)',
                    fontSize: { xs: '9px', sm: '10px' },
                  fontWeight: 600,
                    whiteSpace: 'nowrap',
                }}
              >
                  {checkedInTime.format('h:mm A')}
              </Typography>
              </Box>
            )}
            </GlassBox>

            {/* Chamber Room Status Indicator - Show when checked in */}
            {isCheckedIn && chamberScreenStatus !== 'unknown' && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <ChamberRoomStatusIndicator
                  status={chamberScreenStatus}
                  chamberRoomName={joinedChamberName}
                  compact={true}
                />
              </Box>
            )}

          {isPeekingIn && (
            <Chip
              icon={<VisibilityOffIcon sx={{ fontSize: 12 }} />}
              label="Peeking"
              size="small"
              onClick={handleCheckInPrompt}
              sx={{
                height: '24px',
                fontSize: { xs: '10px', sm: '11px' },
                fontWeight: 600,
                backgroundColor: 'rgba(116, 80, 170, 0.15)',
                color: '#7450aa',
                border: '1px solid rgba(116, 80, 170, 0.3)',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(116, 80, 170, 0.25)',
                  borderColor: 'rgba(116, 80, 170, 0.5)',
                },
              }}
            />
          )}

          {!isCheckedIn && !isPeekingIn && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LoginIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
              onClick={handleCheckInPrompt}
              sx={{
                fontSize: { xs: '11px', sm: '12px' },
                fontWeight: 600,
                padding: { xs: '4px 8px', sm: '6px 12px' },
                borderColor: 'rgba(16, 185, 129, 0.4)',
                color: 'rgba(16, 185, 129, 0.9)',
                '&:hover': {
                  borderColor: 'rgba(16, 185, 129, 0.6)',
                  backgroundColor: 'rgba(16, 185, 129, 0.08)',
                },
              }}
            >
              Check In
            </Button>
          )}

          {/* Calendar Action Buttons - Left of Date Picker (only when calendar tab is selected) */}
          {selectedTab === 'calendar' && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75, ml: 1 }}>
              {onOpenBookedSlotsDrawer && (
                <Tooltip title="View All Booked Slots" arrow>
                  <IconButton
                    size="small"
                    onClick={onOpenBookedSlotsDrawer}
                    sx={{
                      color: 'rgba(67, 56, 202, 0.8)',
                      backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      border: '1px solid rgba(67, 56, 202, 0.2)',
                      padding: '6px',
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.15)',
                        borderColor: 'rgba(67, 56, 202, 0.4)',
                      },
                    }}
                  >
                    <ListIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

              {onRefreshBookings && (
                <Tooltip title="Refresh Bookings" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={onRefreshBookings}
                      disabled={isRefreshing}
                      sx={{
                        color: 'rgba(67, 56, 202, 0.8)',
                        backgroundColor: 'rgba(67, 56, 202, 0.08)',
                        border: '1px solid rgba(67, 56, 202, 0.2)',
                        padding: '6px',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.15)',
                          borderColor: 'rgba(67, 56, 202, 0.4)',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderColor: 'rgba(0, 0, 0, 0.06)',
                          color: 'rgba(0, 0, 0, 0.26)',
                        },
                      }}
                    >
                      {isRefreshing ? (
                        <CircularProgress size={14} sx={{ color: 'rgba(67, 56, 202, 0.7)' }} />
                      ) : (
                        <RefreshIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {eachSlotDurationInMins !== undefined && setEachSlotDurationInMins && (
                <>
                  <Tooltip title="Adjust Slot Duration" arrow>
                    <IconButton
                      size="small"
                      onClick={handleSlotDurationClick}
                      sx={{
                        color: 'rgba(67, 56, 202, 0.8)',
                        backgroundColor: 'rgba(67, 56, 202, 0.08)',
                        border: '1px solid rgba(67, 56, 202, 0.2)',
                        padding: '6px',
                        '&:hover': {
                          backgroundColor: 'rgba(67, 56, 202, 0.15)',
                          borderColor: 'rgba(67, 56, 202, 0.4)',
                        },
                      }}
                    >
                      <HeightIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={slotDurationPopoverOpen}
                    anchorEl={slotDurationAnchorEl}
                    onClose={handleSlotDurationClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    sx={{
                      '& .MuiPopover-paper': {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        overflow: 'visible',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 3,
                        width: 320,
                        backgroundColor: 'white',
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'rgba(67, 56, 202, 0.9)' }}>
                        Slot Duration: {localSlotDuration} minutes
                      </Typography>
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Slider
                          value={localSlotDuration}
                          onChange={handleSlotDurationChange}
                          min={MIN_SLOT_DURATION}
                          max={MAX_SLOT_DURATION}
                          step={5}
                          marks={[
                            { value: 5, label: '5m' },
                            { value: 30, label: '30m' },
                            { value: 60, label: '1h' },
                            { value: 120, label: '2h' },
                          ]}
                          valueLabelDisplay="auto"
                          sx={{
                            color: 'rgba(67, 56, 202, 0.8)',
                            '& .MuiSlider-thumb': {
                              '&:hover, &.Mui-focusVisible': {
                                boxShadow: '0 0 0 8px rgba(67, 56, 202, 0.16)',
                              },
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                        <Button size="small" onClick={handleSlotDurationClose}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSlotDurationApply}
                          sx={{
                            backgroundColor: 'rgba(67, 56, 202, 0.9)',
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 1)',
                            },
                          }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Mobile: Hamburger Menu and Calendar Button (shown on mobile when clinic card is hidden) */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
          {/* Calendar Button for Mobile */}
          {onOpenCalendarDrawer && (
            <Tooltip title="Open Calendar" arrow>
              <IconButton
                size="small"
                onClick={onOpenCalendarDrawer}
                sx={{
                  color: 'rgba(103, 121, 227, 0.8)',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          {onToggleMainDrawer && (
            <Tooltip title="Menu" arrow>
              <IconButton
                size="small"
                onClick={onToggleMainDrawer}
                sx={{
                  color: 'rgba(103, 121, 227, 0.8)',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Center: Compact Date Picker (only when calendar tab is selected) */}
          {selectedTab === 'calendar' && (
          <>
            {/* Desktop: Full date picker */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, justifyContent: 'center', minWidth: 0, flexShrink: 0 }}>
              <GlassBox sx={{ gap: 0.5, padding: '6px 10px' }}>
                <DateNavButton onClick={onPrevClick} size="small">
                  <ArrowLeftIcon sx={{ fontSize: 18 }} />
              </DateNavButton>
              
                <Box 
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '160px',
                    gap: 0.25,
                    px: 1,
                  }}
                >
                <MomentDatePicker
                    date={dayjs(selectedDate, 'DD-MM-YYYY')}
                  onChange={(nextValue) => {
                    const newDate = nextValue?.format('DD-MM-YYYY')
                    setSelectedDate(newDate)
                    window.dispatchEvent(new CustomEvent('calendar-date-change', { detail: { date: newDate } }))
                  }}
                  calendarProps={{
                    minDetail: 'decade',
                    showNeighboringMonth: false,
                    locale: sessionStorage.getItem('languageCode') || 'en',
                  }}
                  clinicSchedules={clinicSchedules}
                  selectedDate={selectedDate}
                    format="DD-MM-YYYY"
                    buttonTextOverride={selectedDate}
                  />
                  {(() => {
                    const today = dayjs().format('DD-MM-YYYY')
                    const yesterday = dayjs().subtract(1, 'day').format('DD-MM-YYYY')
                    const tomorrow = dayjs().add(1, 'day').format('DD-MM-YYYY')
                    
                    let label = null
                    if (selectedDate === today) {
                      label = 'Today'
                    } else if (selectedDate === yesterday) {
                      label = 'Yesterday'
                    } else if (selectedDate === tomorrow) {
                      label = 'Tomorrow'
                    }
                    
                    return label ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6779e3',
                          fontSize: '10px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {label}
                      </Typography>
                    ) : null
                  })()}
              </Box>
              
              {!isToday && (
                  <TodayButton onClick={handleGoToToday} size="small" sx={{ minWidth: 'auto', padding: '6px 10px' }}>
                    <CalendarMonthIcon sx={{ fontSize: 16, mr: 0.75 }} />
                    <Box component="span">Today</Box>
                </TodayButton>
              )}
              
                <DateNavButton onClick={onNextClick} size="small">
                  <ArrowRightIcon sx={{ fontSize: 18 }} />
              </DateNavButton>
            </GlassBox>
            </Box>

            {/* Mobile: Small square date button that opens popover - Hidden on mobile (moved to drawer) */}
            <Box sx={{ display: { xs: 'none', md: 'none' }, alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                onClick={handleOpenCalendarPopover}
                sx={{
                  width: '40px',
                  height: '40px',
                  padding: 0,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  borderRadius: '8px',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    background: 'rgba(103, 121, 227, 0.1)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    gap: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#6779e3',
                      lineHeight: 1,
                    }}
                  >
                    {dayjs(selectedDate, 'DD-MM-YYYY').format('DD')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '8px',
                      fontWeight: 500,
                      color: 'rgba(103, 121, 227, 0.7)',
                      lineHeight: 1,
                      textTransform: 'uppercase',
                    }}
                  >
                    {dayjs(selectedDate, 'DD-MM-YYYY').format('MMM')}
                  </Typography>
                </Box>
              </IconButton>

              {/* Calendar Popover for Mobile */}
              <Popover
                open={calendarPopoverOpen}
                anchorEl={calendarAnchorEl}
                onClose={handleCloseCalendarPopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: '1px solid rgba(103, 121, 227, 0.1)',
                    overflow: 'visible',
                    maxWidth: '90vw',
                  },
                }}
              >
                <Box sx={{ p: 2, minWidth: '280px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <DateNavButton onClick={onPrevClick} size="small">
                      <ArrowLeftIcon sx={{ fontSize: 18 }} />
                    </DateNavButton>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
                      <MomentDatePicker
                        date={dayjs(selectedDate, 'DD-MM-YYYY')}
                        onChange={(nextValue) => {
                          const newDate = nextValue?.format('DD-MM-YYYY')
                          setSelectedDate(newDate)
                          window.dispatchEvent(new CustomEvent('calendar-date-change', { detail: { date: newDate } }))
                        }}
                        calendarProps={{
                          minDetail: 'decade',
                          showNeighboringMonth: false,
                          locale: sessionStorage.getItem('languageCode') || 'en',
                        }}
                        clinicSchedules={clinicSchedules}
                        selectedDate={selectedDate}
                        format="DD-MM-YYYY"
                        buttonTextOverride={selectedDate}
                      />
                      {(() => {
                        const today = dayjs().format('DD-MM-YYYY')
                        const yesterday = dayjs().subtract(1, 'day').format('DD-MM-YYYY')
                        const tomorrow = dayjs().add(1, 'day').format('DD-MM-YYYY')
                        
                        let label = null
                        if (selectedDate === today) {
                          label = 'Today'
                        } else if (selectedDate === yesterday) {
                          label = 'Yesterday'
                        } else if (selectedDate === tomorrow) {
                          label = 'Tomorrow'
                        }
                        
                        return label ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6779e3',
                              fontSize: '10px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            {label}
                          </Typography>
                        ) : null
                      })()}
                    </Box>
                    
                    <DateNavButton onClick={onNextClick} size="small">
                      <ArrowRightIcon sx={{ fontSize: 18 }} />
                    </DateNavButton>
                  </Box>
                  
                  {!isToday && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <TodayButton onClick={() => { handleGoToToday(); handleCloseCalendarPopover(); }} size="small" fullWidth>
                        <CalendarMonthIcon sx={{ fontSize: 16, mr: 0.75 }} />
                        Go to Today
                      </TodayButton>
                    </Box>
                  )}
                </Box>
              </Popover>
            </Box>
          </>
        )}

        {/* Right: Action Buttons, Tabs Menu & Checkout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1.5 }, justifyContent: 'flex-start', minWidth: 0, flexShrink: 0 }}>
          {voiceCallActionNode}
          {/* Patient Search Button - Desktop only */}
          {onOpenPatientSearch && (
            <Tooltip title="Search Patient" arrow>
              <Button
                size="small"
                onClick={onOpenPatientSearch}
                startIcon={<SearchIcon />}
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  color: '#6779e3',
                  backgroundColor: 'rgba(103, 121, 227, 0.1)',
                  border: '1px solid rgba(103, 121, 227, 0.2)',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(103, 121, 227, 0.2)',
                    borderColor: 'rgba(103, 121, 227, 0.4)',
                  },
                }}
              >
                Search Patient
              </Button>
            </Tooltip>
          )}

          {/* Tabs - Show directly when space allows */}
          {availableTabs.length > 0 && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              {availableTabs.map((tab) => {
                const TabIcon = tab.icon
                return (
                  <Tooltip key={tab.id} title={tab.title} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleTabSelect(tab.id)}
                      sx={{
                        color: selectedTab === tab.id ? '#6779e3' : 'rgba(103, 121, 227, 0.6)',
                        backgroundColor: selectedTab === tab.id ? 'rgba(103, 121, 227, 0.15)' : 'rgba(103, 121, 227, 0.08)',
                        border: `1px solid ${selectedTab === tab.id ? 'rgba(103, 121, 227, 0.3)' : 'rgba(103, 121, 227, 0.2)'}`,
                        padding: '6px 10px',
                        '&:hover': {
                          backgroundColor: selectedTab === tab.id ? 'rgba(103, 121, 227, 0.2)' : 'rgba(103, 121, 227, 0.12)',
                          borderColor: 'rgba(103, 121, 227, 0.4)',
                          color: '#6779e3',
                        },
                      }}
                    >
                      <TabIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )
              })}
            </Box>
          )}

          {/* Hamburger Menu for Main Drawer - Desktop only (mobile is shown on left) */}
          {onToggleMainDrawer && (
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Tooltip title="Menu" arrow>
                <IconButton
                  size="small"
                  onClick={onToggleMainDrawer}
                  sx={{
                    color: 'rgba(103, 121, 227, 0.8)',
                    backgroundColor: 'rgba(103, 121, 227, 0.1)',
                    border: '1px solid rgba(103, 121, 227, 0.2)',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 121, 227, 0.2)',
                      borderColor: 'rgba(103, 121, 227, 0.4)',
                    },
                  }}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
        </Box>
          )}

          <Menu
            anchorEl={combinedMenuAnchorEl}
            open={combinedMenuOpen}
            onClose={handleCloseCombinedMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(103, 121, 227, 0.1)',
              },
            }}
          >
            {/* Tabs Section */}
            {availableTabs.length > 0 && (
              <>
                {availableTabs.map((tab) => {
                  const TabIcon = tab.icon
                  return (
                    <MenuItem
                      key={tab.id}
                      onClick={() => handleTabSelect(tab.id)}
                      selected={selectedTab === tab.id}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(103, 121, 227, 0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(103, 121, 227, 0.15)',
                          },
                        },
                      }}
                    >
                      <ListItemIcon>
                        <TabIcon sx={{ fontSize: 20, color: selectedTab === tab.id ? '#6779e3' : 'rgba(103, 121, 227, 0.6)' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={tab.title}
                        primaryTypographyProps={{
                          fontWeight: selectedTab === tab.id ? 600 : 400,
                          color: selectedTab === tab.id ? '#6779e3' : 'rgba(103, 121, 227, 0.7)',
                        }}
                      />
                    </MenuItem>
                  )
                })}
              </>
            )}
          </Menu>

          {/* Checkout Button - Always visible */}
        <CheckoutButtonWrapper>
          <CheckoutButtonComponent
            text="Checkout"
            additionalFunctionBeforeAction={() => {
              if (chamberScreenStateNow === 'SESSION_RUNNING') {
                leaveThisSession()
              }
            }}
            showExitButtonIfNotCheckedIn={true}
            styleProp={{
                fontSize: { xs: '11px', sm: '12px', md: '13px' },
              fontWeight: 700,
                padding: { xs: '4px 10px', sm: '5px 12px' },
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
                borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                backgroundColor: '#dc2626',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
              },
            }}
          />
        </CheckoutButtonWrapper>
        </Box>
      </HeaderContainer>

      {/* Spacer for fixed header */}
      <Box sx={{ height: { xs: '32px', sm: '34px', md: '36px' } }} />

      {/* Check-in Dialog */}
      {checkInDialogProps && (
        <CheckInDialogComponent
          key={`checkin-dialog-${dialogRoomData?.id || 'default'}`}
          {...checkInDialogProps}
        />
      )}

      <CheckinRequestPromptDialog
        open={requestCheckoutDialogOpen}
        roomData={requestCheckoutRoomData}
        clinicLocationData={chosenClinicData}
        onClose={() => {
          if (!requestCheckoutLoading) {
            setRequestCheckoutDialogOpen(false)
            setRequestCheckoutRoomData(null)
          }
        }}
        onSubmit={handleSubmitCheckoutRequest}
        loading={requestCheckoutLoading}
      />
    </>
  )
}
